import React, { Fragment, useState, useEffect } from "react";
import AddressCard from "./AddressCard";
import AddressForm from "./AddressForm";
import { getAddresses, deleteAddress } from "../../api/apiServices";
import { useSnackbar } from "notistack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

const Address = ({ isEdit, onAddressSelect, showRadio }) => {
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editAddress, setEditAddress] = useState(null);
  const [addresses, setAddresses] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchAddresses = async () => {
    try {
      const response = await getAddresses();
      setAddresses(response.data.data);
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, [showAddressForm]);

  const handleFormSubmitSuccess = () => {
    setShowAddressForm(false);
  };

  const handleAddressSelect = (address) => {
    onAddressSelect(address);
  };

  const handleRemoveClick = (address_id) => {
    setAddressToDelete(address_id);
  };

  const handleDeleteConfirmClose = () => {
    if (!removingId) setAddressToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!addressToDelete) return;
    try {
      setRemovingId(addressToDelete);
      const res = await deleteAddress(addressToDelete);
      if (res?.data?.success) {
        setAddressToDelete(null);
        enqueueSnackbar(res.data.message || "Address removed", { variant: "success" });
        await fetchAddresses();
      } else {
        enqueueSnackbar(res?.data?.message || "Could not remove address", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Could not remove address. Please try again.", { variant: "error" });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Dialog
        open={addressToDelete != null}
        onClose={handleDeleteConfirmClose}
        aria-labelledby="delete-address-dialog-title"
        aria-describedby="delete-address-dialog-description"
      >
        <DialogTitle id="delete-address-dialog-title">
          Remove address?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-address-dialog-description">
            This address will be removed from your saved addresses. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose} disabled={!!removingId}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={!!removingId}
          >
            {removingId ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {showAddressForm ? (
        <div className="rounded-xl bg-white shadow-md border border-gray-100 overflow-hidden">
          <AddressForm
            setShowAddressForm={setShowAddressForm}
            editAddress={editAddress}
            setEditAddress={setEditAddress}
            onSuccess={() => {
              handleFormSubmitSuccess();
              setEditAddress(null);
            }}
          />
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {!isEdit && <h2 className="text-xl font-semibold text-gray-800">Saved addresses</h2>}
          <button
            type="button"
            className="btn-rounded-primary text-sm px-5 py-2.5 min-h-[44px] sm:min-h-0 w-full sm:w-auto"
            onClick={() => {
              setShowAddressForm(true);
              setEditAddress(false);
            }}
          >
            + Add New Address
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        {Array.isArray(addresses) &&
          addresses?.map((address) => (
            <Fragment key={address.address_id}>
              <AddressCard
                address={address}
                showInput={showRadio}
                isEdit={isEdit}
                editAddress={editAddress}
                setEditAddress={setEditAddress}
                setShowAddressForm={setShowAddressForm}
                onAddressSelect={handleAddressSelect}
                onRemove={handleRemoveClick}
                isRemoving={removingId === address.address_id}
              />
            </Fragment>
          ))}
      </div>
    </div>
  );
};

export default Address;
